import type { CaptureResult } from 'posthog-js'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const EMAIL_PATTERN = /[\p{L}\p{M}\p{N}_.%+-]+@[\p{L}\p{M}\p{N}-]+(?:\.[\p{L}\p{M}\p{N}-]+)+/u
const HEX_CREDENTIAL_PATTERN = /^[a-f0-9]{32,}$/i
const COMPACT_JOSE_PATTERN = /^(?:[\w-]{8,}\.[\w-]*\.[\w-]{8,}\.[\w-]*\.[\w-]{8,}|[\w-]{8,}\.[\w-]*\.[\w-]{8,})$/
const KNOWN_CREDENTIAL_PATTERN = /^(?:(?:sk|pk)[-_][\w-]{8,}|(?:phc|gh[oprsu])_[\w-]{8,}|github_pat_\w{8,}|xox[baprs]-[a-z0-9-]{8,})$/i

function safeUrl(value: string | undefined, base?: string): URL | undefined {
  if (!value)
    return undefined
  try {
    return new URL(value, base)
  }
  catch {
    return undefined
  }
}

/** Reject credential-shaped values without rejecting ordinary campaign text. */
export function containsOpaqueAnalyticsToken(value: string): boolean {
  const normalized = value.normalize('NFKC').trim()
  if (
    HEX_CREDENTIAL_PATTERN.test(normalized)
    || COMPACT_JOSE_PATTERN.test(normalized)
    || KNOWN_CREDENTIAL_PATTERN.test(normalized)
  ) {
    return true
  }

  return (normalized.match(/[a-z0-9]{32,}/gi) ?? [])
    .some(token => /[a-z]/i.test(token) && /\d/.test(token))
}

function isSensitivePathSegment(segment: string): boolean {
  let decoded = segment
  try {
    decoded = decodeURIComponent(decoded)
  }
  catch {
    return true
  }
  if (UUID_PATTERN.test(decoded))
    return false
  return EMAIL_PATTERN.test(decoded) || containsOpaqueAnalyticsToken(decoded)
}

export function sanitizeAnalyticsPath(value: string): string {
  let path = value.split(/[?#]/, 1)[0]
  path = path
    .replace(/^\/cli\/auth\/[^/]+(?=\/|$)/, '/cli/auth/:authRequestId')
    .replace(/^\/apps\/[^/]+(?=\/|$)/, '/apps/:id')
    .replace(/^\/avatars\/(?!create(?:\/|$)|creations(?:\/|$)|custom(?:\/|$)|library(?:\/|$)|material-guide(?:\/|$)|personal(?:\/|$)|slots(?:\/|$)|units(?:\/|$))[^/]+(?=\/|$)/, '/avatars/:id')
    .replace(/^\/download\/[^/]+(?=\/|$)/, '/download/:id')
    .replace(/^\/feedback\/(?!create(?:\/|$))[^/]+(?=\/|$)/, '/feedback/:id')

  return path.split('/').map(segment => isSensitivePathSegment(segment) ? ':redacted' : segment).join('/')
}

/**
 * Keep only the URL parts needed for page-path analysis. PostHog persists UTM
 * values as dedicated properties, so analytics URLs do not need a query copy.
 */
export function sanitizeAnalyticsUrl(value: string): string {
  const parsed = safeUrl(value, typeof window !== 'undefined' ? window.location.origin : 'https://app.spatius.ai')
  if (!parsed)
    return sanitizeAnalyticsPath(value).slice(0, 500)

  parsed.username = ''
  parsed.password = ''
  parsed.pathname = sanitizeAnalyticsPath(parsed.pathname)
  parsed.search = ''
  parsed.hash = ''
  return parsed.toString().slice(0, 1000)
}

function sanitizePropertyBag(properties: Record<string, unknown> | undefined, eventProperties = false): Record<string, unknown> | undefined {
  if (!properties)
    return undefined
  const sanitized = { ...properties }
  for (const key of ['$current_url', '$initial_current_url', '$session_entry_url']) {
    if (typeof sanitized[key] === 'string')
      sanitized[key] = sanitizeAnalyticsUrl(sanitized[key] as string)
  }
  for (const key of ['$referrer', '$initial_referrer', '$session_entry_referrer']) {
    const value = sanitized[key]
    if (typeof value !== 'string' || value === '$direct')
      continue
    const referrer = safeUrl(value)
    sanitized[key] = referrer ? `${referrer.origin}/` : ''
  }
  for (const [key, value] of Object.entries(sanitized)) {
    if (typeof value === 'string' && (key.endsWith('_path') || key.endsWith('pathname') || key === 'route_template'))
      sanitized[key] = sanitizeAnalyticsPath(value)
  }
  delete sanitized.ph_keyword
  delete sanitized.$initial_campaign_params
  delete sanitized.auth_request_id
  if (eventProperties) {
    delete sanitized.email
    delete sanitized.username
    delete sanitized.nickname
  }
  return sanitized
}

export function sanitizePostHogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event)
    return null
  return {
    ...event,
    properties: sanitizePropertyBag(event.properties, true) ?? {},
    $set: sanitizePropertyBag(event.$set),
    $set_once: sanitizePropertyBag(event.$set_once),
  }
}
